import { BaseLogo, ProductPrice } from "../../core/models/entities/BaseLogo";
import { Design, DesignLogo } from "../../core/models/entities/Design";
import { Price } from "../../core/models/entities/Price";
import {
  CartItem,
  CartItemLogo,
  CartItemDesign,
} from "../../presentation/screens/order-create/models/CartItem";

/**
 * Gets the actual minimum quantity from the logo's pricing structure.
 */
export function getLogoActualMinQuantity(logo: BaseLogo): number {
  const prices = logo?.product?.prices;

  if (!prices || prices.length === 0) {
    return 1; // Allow any quantity if no pricing info
  }

  const sortedPrices = [...prices].sort(
    (a, b) => a.minQuantity - b.minQuantity
  );
  const minQuantity = sortedPrices[0]?.minQuantity || 1;
  return minQuantity;
}

/**
 * Gets the display minimum quantity for a logo (for Stepper initialization).
 * Returns the actual minimum quantity, or 10 as default if actual minimum is less than 10.
 */
export function getLogoMinQuantity(logo: BaseLogo): number {
  const actualMin = getLogoActualMinQuantity(logo);

  // Return the actual minimum, but at least 10
  return Math.max(actualMin, 10);
}

/**
 * Gets the actual minimum quantity from the design's pricing structure.
 */
export function getDesignActualMinQuantity(design: Design): number {
  const prices = design?.product?.totalPrices;
  const productMin = prices && prices.length > 0
    ? [...prices].sort((a, b) => a.minQuantity - b.minQuantity)[0]?.minQuantity || 1
    : 1;

  const logoMins = (design?.designLogos || [])
    .map((dl: DesignLogo) => dl.logo)
    .filter((logo) => !!logo)
    .map((logo) => getLogoActualMinQuantity(logo as BaseLogo));

  const highestLogoMin = logoMins.length > 0 ? Math.max(...logoMins) : 1;

  return Math.max(productMin, highestLogoMin);
}

/**
 * Gets the display minimum quantity for a design (for Stepper initialization).
 * Returns the actual minimum quantity, or 10 as default if actual minimum is less than 10.
 */
export function getDesignMinQuantity(design: Design): number {
  const actualMin = getDesignActualMinQuantity(design);

  // Return the actual minimum, but at least 10
  return Math.max(actualMin, 10);
}

/**
 * Checks if the given quantity meets the actual minimum requirement for a logo.
 */
export function isValidLogoQuantity(logo: BaseLogo, quantity: number): boolean {
  const actualMinQuantity = getLogoActualMinQuantity(logo);
  return quantity >= actualMinQuantity;
}

/**
 * Checks if the given quantity meets the actual minimum requirement for a design.
 */
export function isValidDesignQuantity(
  design: Design,
  quantity: number
): boolean {
  const actualMinQuantity = getDesignActualMinQuantity(design);
  return quantity >= actualMinQuantity;
}

/**
 * Validation result for order minimum quantity check.
 */
export interface OrderValidationResult {
  isValid: boolean;
  invalidLogos: Array<{
    logoId: number;
    logoTitle: string;
    logoSku: string;
    totalQuantity: number;
    minimumRequired: number;
  }>;
}

/**
 * Validates that all logos in the order meet their minimum quantity requirements.
 * Aggregates quantities across multiple order lines for the same logo.
 */
export function validateOrderMinimumQuantities(
  orderLines: CartItem[]
): OrderValidationResult {
  // Group order lines by logo ID and calculate total quantities
  const logoQuantities = new Map<
    number,
    {
      logo: BaseLogo;
      totalQuantity: number;
    }
  >();

  // Process logo order lines
  const logoOrderLines = orderLines.filter(
    (line) => line.type === "Logo"
  ) as CartItemLogo[];

  for (const orderLine of logoOrderLines) {
    const logoId = orderLine.logoId;
    const logo = orderLine.baseLogo;

    if (!logoId || !logo) continue;

    // For numbersets, check individual number quantities instead of parent logo totals
    if (logo.logoType === "NumberLogo") {
      // For numbersets, each individual number (0-9) needs to meet minimum requirements
      const deliveryQuantities = orderLine.deliveryQuantities || [];
      const storageQuantities = orderLine.storageQuantities || [];

      // Check each number (0-9) individually
      for (let i = 0; i <= 9; i++) {
        const numberStr = i.toString();
        const deliveryQty =
          deliveryQuantities.find((q) => q.label === numberStr)?.quantity || 0;
        const storageQty =
          storageQuantities.find((q) => q.label === numberStr)?.quantity || 0;
        const totalQty = deliveryQty + storageQty;

        if (totalQty > 0) {
          // Use a unique identifier for each number in the numberset
          const numberLogoId = logoId * 10 + i; // Create unique ID for validation

          if (logoQuantities.has(numberLogoId)) {
            const existing = logoQuantities.get(numberLogoId)!;
            existing.totalQuantity += totalQty;
          } else {
            logoQuantities.set(numberLogoId, {
              logo: logo,
              totalQuantity: totalQty,
            });
          }
        }
      }
    } else {
      // For regular logos, use the standard approach
      const quantity = (orderLine.toDelivery || 0) + (orderLine.toStorage || 0);

      if (logoQuantities.has(logoId)) {
        const existing = logoQuantities.get(logoId)!;
        existing.totalQuantity += quantity;
      } else {
        logoQuantities.set(logoId, {
          logo: logo,
          totalQuantity: quantity,
        });
      }
    }
  }

  // Process design order lines that contain logos
  const designOrderLines = orderLines.filter(
    (line) => line.type === "Design"
  ) as CartItemDesign[];

  for (const orderLine of designOrderLines) {
    for (const instance of orderLine.instances) {
      const instanceQuantity =
        (instance.toDelivery || 0) + (instance.toStorage || 0);

      for (const instanceLogo of instance.logos) {
        const logoId = instanceLogo.logoId;

        if (!logoId) {
          continue;
        }

        // Find the logo from the design's designLogos to get BaseLogo reference
        const logo = orderLine.design.designLogos?.find(
          (designLogo: DesignLogo) => designLogo.logoId === logoId
        )?.logo;

        if (!logo) {
          continue;
        }

        if (logoQuantities.has(logoId)) {
          const existing = logoQuantities.get(logoId)!;
          existing.totalQuantity += instanceQuantity;
        } else {
          logoQuantities.set(logoId, {
            logo: logo,
            totalQuantity: instanceQuantity,
          });
        }
      }
    }
  }

  // Check each logo against its minimum quantity requirement
  const invalidLogos: Array<{
    logoId: number;
    logoTitle: string;
    logoSku: string;
    totalQuantity: number;
    minimumRequired: number;
  }> = [];

  logoQuantities.forEach(({ logo, totalQuantity }, logoId) => {
    const minimumRequired = getLogoActualMinQuantity(logo);

    if (totalQuantity < minimumRequired) {
      // For numbersets, extract the individual number from the unique ID
      if (logo.logoType === "NumberLogo" && logoId > logo.id) {
        const numberValue = logoId % 10;
        invalidLogos.push({
          logoId: logo.id, // Use the original logo ID for the error
          logoTitle: `${logo.title || "Numberset"} - Number ${numberValue}`,
          logoSku: logo.product?.sku || `SKU-${logo.id}`,
          totalQuantity,
          minimumRequired,
        });
      } else {
        // For regular logos
        invalidLogos.push({
          logoId,
          logoTitle: logo.title || `Logo ${logoId}`,
          logoSku: logo.product?.sku || `SKU-${logoId}`,
          totalQuantity,
          minimumRequired,
        });
      }
    } else {
      //console.log(`Validation passed for logo ${logo.title}`);
    }
  });

  return {
    isValid: invalidLogos.length === 0,
    invalidLogos,
  };
}
